import Graph from 'graphology';
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import { GRAPH_DEFAULTS } from '@/components/nodeGraph/config/graphConfig';
import { Cluster } from '@/components/nodeGraph/controls/ClusterWeightControls/types';
import {
  DefaultAttributeWeights,
  DefaultVisibilityState,
  EntityWeightConfigs as DefaultEntityWeightConfigs,
} from '@/components/nodeGraph/utils/constants';
import {
  AttributeWeight,
  GraphEntityType,
  VisibilityState,
} from '@/components/nodeGraph/utils/types';
import { ResourceType } from '@/types';
import { useStorage } from '@/utils/storage/useStorage.util';

/**
 * GraphStateContext - UI State Management for Graph Interactions
 *
 * This context manages all interactive UI state for the node graph including:
 * - Node and cluster selection
 * - Entity type selection (Assets, Risks, Technologies, etc.)
 * - Attribute visibility controls
 * - Clustering weights and active clusters
 * - Configuration settings
 *
 * Uses useReducer for predictable state transitions and optimal re-renders.
 */

/**
 * Selection state - tracks selected nodes and clusters
 */
interface SelectionState {
  nodes: Set<string>;
  cluster: string | null;
  assetKey: string | null;
}

/**
 * Entity state - tracks which entity type is being visualized
 */
interface EntityState {
  type: GraphEntityType;
  selectedTypes: GraphEntityType[];
}

/**
 * Visibility state - controls which nodes are visible
 */
interface VisibilityStateConfig {
  state: VisibilityState;
}

/**
 * Clustering state - manages attribute weights and active clusters
 */
interface ClusteringState {
  weights: AttributeWeight[];
  clusters: Cluster[];
}

/**
 * Configuration state - user preferences and settings
 */
interface ConfigState {
  maxNodes: number;
  setMaxNodes: (maxNodes: number) => void;
  showOutdatedWarning: boolean;
}

/**
 * Graph instance state - tracks graph and layout instances
 */
interface InstanceState {
  graphRef: Graph | null;
  isInitialLoad: boolean;
  existingPositions: Map<string, { x: number; y: number }>;
}

/**
 * Complete graph state
 */
interface GraphState {
  selection: SelectionState;
  entity: EntityState;
  visibility: VisibilityStateConfig;
  clustering: ClusteringState;
  config: ConfigState;
  instance: InstanceState;
}

/**
 * Action types for state updates
 */
type GraphStateAction =
  | { type: 'SELECT_NODES'; payload: { nodes: Set<string> } }
  | {
      type: 'SELECT_CLUSTER';
      payload: { cluster: string; nodes: Set<string> };
    }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SET_ASSET_KEY'; payload: { assetKey: string | null } }
  | { type: 'SET_ENTITY_TYPE'; payload: { entityType: GraphEntityType } }
  | {
      type: 'SET_SELECTED_TYPES';
      payload: { selectedTypes: GraphEntityType[] };
    }
  | { type: 'SET_VISIBILITY'; payload: { state: VisibilityState } }
  | { type: 'SET_WEIGHT'; payload: { name: string; weight: number } }
  | { type: 'SET_CLUSTERS'; payload: { clusters: Cluster[] } }
  | { type: 'SET_GRAPH_REF'; payload: { graphRef: Graph | null } }
  | { type: 'SET_INITIAL_LOAD'; payload: { isInitialLoad: boolean } }
  | { type: 'SET_SHOW_OUTDATED_WARNING'; payload: { show: boolean } };

/**
 * Reducer for managing graph state transitions
 */
const graphStateReducer = (
  state: GraphState,
  action: GraphStateAction
): GraphState => {
  switch (action.type) {
    case 'SELECT_NODES':
      return {
        ...state,
        selection: {
          ...state.selection,
          nodes: action.payload.nodes,
        },
      };

    case 'SELECT_CLUSTER':
      return {
        ...state,
        selection: {
          ...state.selection,
          nodes: action.payload.nodes,
          cluster: action.payload.cluster,
        },
      };

    case 'CLEAR_SELECTION':
      return {
        ...state,
        selection: {
          nodes: new Set(),
          cluster: null,
          assetKey: null,
        },
      };

    case 'SET_ASSET_KEY':
      return {
        ...state,
        selection: {
          ...state.selection,
          assetKey: action.payload.assetKey,
        },
      };

    case 'SET_ENTITY_TYPE':
      return {
        ...state,
        entity: {
          ...state.entity,
          type: action.payload.entityType,
        },
      };

    case 'SET_SELECTED_TYPES':
      return {
        ...state,
        entity: {
          ...state.entity,
          selectedTypes: action.payload.selectedTypes,
        },
      };

    case 'SET_VISIBILITY':
      return {
        ...state,
        visibility: {
          state: action.payload.state,
        },
      };

    case 'SET_WEIGHT':
      return {
        ...state,
        clustering: {
          ...state.clustering,
          weights: state.clustering.weights.map(w =>
            w.name === action.payload.name
              ? { ...w, weight: action.payload.weight }
              : w
          ),
        },
      };

    case 'SET_CLUSTERS':
      return {
        ...state,
        clustering: {
          ...state.clustering,
          clusters: action.payload.clusters,
        },
      };

    case 'SET_GRAPH_REF':
      return {
        ...state,
        instance: {
          ...state.instance,
          graphRef: action.payload.graphRef,
        },
      };

    case 'SET_INITIAL_LOAD':
      return {
        ...state,
        instance: {
          ...state.instance,
          isInitialLoad: action.payload.isInitialLoad,
        },
      };

    case 'SET_SHOW_OUTDATED_WARNING':
      return {
        ...state,
        config: {
          ...state.config,
          showOutdatedWarning: action.payload.show,
        },
      };

    default:
      return state;
  }
};

/**
 * Context value shape
 */
interface GraphStateContextValue {
  selection: {
    nodes: Set<string>;
    cluster: string | null;
    assetKey: string | null;
    setNodes: (nodes: Set<string>) => void;
    setCluster: (cluster: string | null, nodes: Set<string>) => void;
    setAssetKey: (assetKey: string | null) => void;
    clear: () => void;
  };

  entity: {
    type: GraphEntityType;
    selectedTypes: GraphEntityType[];
    setType: (type: GraphEntityType) => void;
    setSelectedTypes: (types: GraphEntityType[]) => void;
  };

  visibility: {
    state: VisibilityState;
    setState: (state: VisibilityState) => void;
  };

  clustering: {
    weights: AttributeWeight[];
    setWeight: (name: string, weight: number) => void;
    clusters: Cluster[];
    setClusters: (clusters: Cluster[]) => void;
  };

  config: {
    maxNodes: number;
    setMaxNodes: (maxNodes: number) => void;
    showOutdatedWarning: boolean;
    setShowOutdatedWarning: (show: boolean) => void;
  };

  instance: {
    graphRef: Graph | null;
    setGraphRef: (graphRef: Graph | null) => void;
    isInitialLoad: boolean;
    setIsInitialLoad: (isInitialLoad: boolean) => void;
    existingPositions: Map<string, { x: number; y: number }>;
  };
}

const GraphStateContext = createContext<GraphStateContextValue | null>(null);

/**
 * Initial state for the graph
 */
const createInitialState = (
  visibleState: VisibilityState,
  maxNodes: number,
  setMaxNodes: (maxNodes: number) => void
): GraphState => ({
  selection: {
    nodes: new Set(),
    cluster: null,
    assetKey: null,
  },
  entity: {
    type: ResourceType.Asset,
    selectedTypes: [
      ResourceType.Asset,
      ResourceType.Seed,
      ResourceType.Attribute,
      ResourceType.Risk,
      ResourceType.Technology,
      ResourceType.Webpage,
    ],
  },
  visibility: {
    state: visibleState,
  },
  clustering: {
    weights: DefaultEntityWeightConfigs[ResourceType.Asset] || [],
    clusters: [],
  },
  config: {
    maxNodes,
    setMaxNodes,
    showOutdatedWarning: false,
  },
  instance: {
    graphRef: null,
    isInitialLoad: true,
    existingPositions: new Map(),
  },
});

export interface GraphStateProviderProps {
  children: ReactNode;
}

/**
 * GraphStateProvider - Manages all UI state for graph interactions
 *
 * This provider uses useReducer for predictable state management with clear
 * action types for all state transitions. It manages:
 *
 * 1. Selection: Node, cluster, and asset selection state
 * 2. Entity: Active entity type and visible entity types
 * 3. Visibility: Which nodes and attributes are visible
 * 4. Clustering: Attribute weights and computed clusters
 * 5. Config: User preferences (max nodes, warnings)
 * 6. Instance: Graph instance and initialization state
 *
 * Uses localStorage for persistence of:
 * - Visibility state
 * - Max nodes configuration
 *
 * Usage:
 * ```tsx
 * <GraphStateProvider>
 *   <GraphComponents />
 * </GraphStateProvider>
 * ```
 *
 * Access state via:
 * ```tsx
 * const { selection, entity, visibility } = useGraphStateContext();
 * ```
 */
export const GraphStateProvider: FC<GraphStateProviderProps> = ({
  children,
}) => {
  // Persistent state from localStorage
  const [visibleState, setVisibleState] = useStorage<VisibilityState>(
    {
      key: 'nodeGraph.visibilityState.v1',
    },
    DefaultVisibilityState
  );

  const [maxNodes, setMaxNodes] = useStorage<number>(
    {
      key: 'nodeGraph.maxNodes.v1',
    },
    GRAPH_DEFAULTS.maxNodes
  );

  // Initialize state with persistent values
  const [state, dispatch] = useReducer(
    graphStateReducer,
    createInitialState(visibleState, maxNodes, setMaxNodes)
  );

  // Weight management state - separate from reducer for easier updates
  const [entityWeightConfigs, setEntityWeightConfigs] = useState<
    Record<GraphEntityType, AttributeWeight[]>
  >(DefaultEntityWeightConfigs);

  // Selection actions
  const setNodes = useCallback((nodes: Set<string>) => {
    dispatch({ type: 'SELECT_NODES', payload: { nodes } });
  }, []);

  const setCluster = useCallback(
    (cluster: string | null, nodes: Set<string>) => {
      if (cluster === null) {
        dispatch({ type: 'CLEAR_SELECTION' });
      } else {
        dispatch({ type: 'SELECT_CLUSTER', payload: { cluster, nodes } });
      }
    },
    []
  );

  const setAssetKey = useCallback((assetKey: string | null) => {
    dispatch({ type: 'SET_ASSET_KEY', payload: { assetKey } });
  }, []);

  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, []);

  // Entity actions - synchronous for immediate updates
  const setType = useCallback((entityType: GraphEntityType) => {
    dispatch({ type: 'SET_ENTITY_TYPE', payload: { entityType } });
  }, []);

  const setSelectedTypes = useCallback((selectedTypes: GraphEntityType[]) => {
    dispatch({ type: 'SET_SELECTED_TYPES', payload: { selectedTypes } });
  }, []);

  // Visibility actions
  const setVisibilityState = useCallback(
    (newState: VisibilityState) => {
      setVisibleState(newState); // Persist to localStorage
      dispatch({ type: 'SET_VISIBILITY', payload: { state: newState } });
    },
    [setVisibleState]
  );

  // Clustering/Weight actions - synchronous for immediate updates
  const handleWeightChange = useCallback(
    (name: string, weight: number) => {
      setEntityWeightConfigs(prev => {
        const currentWeights =
          prev[state.entity.type] || DefaultAttributeWeights;
        const updatedWeights = currentWeights.map(attr =>
          attr.name === name ? { ...attr, weight } : attr
        );

        return {
          ...prev,
          [state.entity.type]: updatedWeights,
        };
      });
    },
    [state.entity.type]
  );

  const setClusters = useCallback((clusters: Cluster[]) => {
    dispatch({ type: 'SET_CLUSTERS', payload: { clusters } });
  }, []);

  // Config actions
  const handleSetMaxNodes = useCallback(
    (nodes: number) => {
      setMaxNodes(nodes); // Persist to localStorage
      // Note: maxNodes is in config.setMaxNodes, not in reducer
    },
    [setMaxNodes]
  );

  const setShowOutdatedWarning = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_OUTDATED_WARNING', payload: { show } });
  }, []);

  // Instance actions
  const setGraphRef = useCallback((graphRef: Graph | null) => {
    dispatch({ type: 'SET_GRAPH_REF', payload: { graphRef } });
  }, []);

  const setIsInitialLoad = useCallback((isInitialLoad: boolean) => {
    dispatch({ type: 'SET_INITIAL_LOAD', payload: { isInitialLoad } });
  }, []);

  // Auto-initialize weights for selected entity type
  /* eslint-disable react-hooks/set-state-in-effect -- sync graph weight configuration from entity type changes */
  useEffect(() => {
    const currentEntityType = state.entity.type;
    if (!entityWeightConfigs[currentEntityType]) {
      const defaultWeights =
        DefaultEntityWeightConfigs[currentEntityType] ||
        DefaultAttributeWeights;
      setEntityWeightConfigs(prev => ({
        ...prev,
        [currentEntityType]: defaultWeights,
      }));
    }
  }, [state.entity.type, entityWeightConfigs]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Auto-select severity clustering when switching to Vulnerabilities
  // Auto-select vendor clustering when switching to Technologies
  /* eslint-disable react-hooks/set-state-in-effect -- sync graph weight configuration from entity type changes */
  useEffect(() => {
    const currentEntityType = state.entity.type;

    // Apply auto-selection for Risk (Vulnerability) entity type
    if (currentEntityType === ResourceType.Risk) {
      const currentWeights = entityWeightConfigs[currentEntityType];
      const severityWeight = currentWeights?.find(w => w.name === 'severity');

      if (severityWeight && severityWeight.weight !== 20) {
        const updatedWeights = currentWeights.map(attr =>
          attr.name === 'severity'
            ? { ...attr, weight: 20 }
            : { ...attr, weight: 0 }
        );

        setEntityWeightConfigs(prev => ({
          ...prev,
          [currentEntityType]: updatedWeights,
        }));
      }
    }
    // Apply auto-selection for Technology entity type
    else if (currentEntityType === ResourceType.Technology) {
      const currentWeights = entityWeightConfigs[currentEntityType];
      const vendorWeight = currentWeights?.find(w => w.name === 'vendor');

      if (vendorWeight && vendorWeight.weight !== 20) {
        const updatedWeights = currentWeights.map(attr =>
          attr.name === 'vendor'
            ? { ...attr, weight: 20 }
            : { ...attr, weight: 0 }
        );

        setEntityWeightConfigs(prev => ({
          ...prev,
          [currentEntityType]: updatedWeights,
        }));
      }
    }
  }, [state.entity.type, entityWeightConfigs]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Compute current weights for active entity type
  const currentWeights = useMemo(
    () => entityWeightConfigs[state.entity.type] || [],
    [entityWeightConfigs, state.entity.type]
  );

  // Memoize context value
  const value = useMemo<GraphStateContextValue>(
    () => ({
      selection: {
        nodes: state.selection.nodes,
        cluster: state.selection.cluster,
        assetKey: state.selection.assetKey,
        setNodes,
        setCluster,
        setAssetKey,
        clear: clearSelection,
      },
      entity: {
        type: state.entity.type,
        selectedTypes: state.entity.selectedTypes,
        setType,
        setSelectedTypes,
      },
      visibility: {
        state: state.visibility.state,
        setState: setVisibilityState,
      },
      clustering: {
        weights: currentWeights,
        setWeight: handleWeightChange,
        clusters: state.clustering.clusters,
        setClusters,
      },
      config: {
        maxNodes,
        setMaxNodes: handleSetMaxNodes,
        showOutdatedWarning: state.config.showOutdatedWarning,
        setShowOutdatedWarning,
      },
      instance: {
        graphRef: state.instance.graphRef,
        setGraphRef,
        isInitialLoad: state.instance.isInitialLoad,
        setIsInitialLoad,
        existingPositions: state.instance.existingPositions,
      },
    }),
    [
      state,
      maxNodes,
      currentWeights,
      setNodes,
      setCluster,
      setAssetKey,
      clearSelection,
      setType,
      setSelectedTypes,
      setVisibilityState,
      handleWeightChange,
      setClusters,
      handleSetMaxNodes,
      setShowOutdatedWarning,
      setGraphRef,
      setIsInitialLoad,
    ]
  );

  return (
    <GraphStateContext.Provider value={value}>
      {children}
    </GraphStateContext.Provider>
  );
};

/**
 * useGraphStateContext - Access graph UI state from context
 *
 * This hook provides access to all interactive UI state including selection,
 * visibility, clustering, and configuration. Must be used within a GraphStateProvider.
 *
 * @throws Error if used outside GraphStateProvider
 * @returns GraphStateContextValue containing all state and actions
 *
 * @example
 * ```tsx
 * const { selection, entity, clustering } = useGraphStateContext();
 *
 * // Select nodes
 * selection.setNodes(new Set(['asset1', 'asset2']));
 *
 * // Change entity type
 * entity.setType(ResourceType.Risk);
 *
 * // Update weight
 * clustering.setWeight('country', 20);
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useGraphStateContext = (): GraphStateContextValue => {
  const context = useContext(GraphStateContext);
  if (!context) {
    throw new Error(
      'useGraphStateContext must be used within GraphStateProvider'
    );
  }
  return context;
};
